import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  NgZone,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import {
  DiffService,
  SplitDiffResult,
  AlignedLine,
  JsonDiffResult,
} from '../../services/diff/diff.service';

export type DiffViewMode = 'inline' | 'side-by-side';

interface ChangeGroup {
  startIndex: number;
  endIndex: number;
}

interface VirtualLine {
  line: AlignedLine;
  index: number;
}

const ROW_HEIGHT = 20;
const BUFFER_ROWS = 30;

@Component({
  standalone: false,
  selector: 'irembogov-json-diff-viewer',
  templateUrl: './irembogov-json-diff-viewer.component.html',
  styleUrls: ['./irembogov-json-diff-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IrembogovJsonDiffViewerComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  @Input() oldData: unknown;
  @Input() newData: unknown;
  @Input() oldLabel = 'Previous (Current Live)';
  @Input() newLabel = 'New (Change Request)';
  @Input() sectionTitle = '';
  /** When true, defers diff computation and shows a loading spinner. */
  @Input() loading = false;
  @Input() enableLineComment = false;
  @Output() lineComment = new EventEmitter<string>();

  get effectiveOldLabel(): string {
    return this.oldData !== null && this.oldData !== undefined
      ? this.oldLabel
      : 'No previous version';
  }

  @ViewChild('oldPanel') oldPanelRef!: ElementRef<HTMLDivElement>;
  @ViewChild('newPanel') newPanelRef!: ElementRef<HTMLDivElement>;

  splitResult: SplitDiffResult | null = null;
  propResult: JsonDiffResult | null = null;
  hasDifferences = false;
  isComputing = false;

  changeGroups: ChangeGroup[] = [];
  currentChangeIndex = -1;

  /** Virtual scroll state */
  totalHeight = 0;
  topOffset = 0;
  visibleLines: VirtualLine[] = [];
  private visibleStart = 0;
  private visibleEnd = 0;

  private syncing = false;
  private oldScrollListener: (() => void) | null = null;
  private newScrollListener: (() => void) | null = null;
  private setupScrollTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private rafId: number | null = null;
  private computeGeneration = 0;

  constructor(
    private readonly diffService: DiffService,
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.loading) return;

    const loadingJustFinished =
      changes['loading'] &&
      changes['loading'].previousValue === true &&
      !changes['loading'].currentValue;

    if (loadingJustFinished || changes['oldData'] || changes['newData']) {
      this.computeDiffAsync();
    }
  }

  ngAfterViewInit(): void {
    this.setupSyncScroll();
  }

  ngOnDestroy(): void {
    this.teardownSyncScroll();
    this.computeGeneration++;
    if (this.setupScrollTimeoutId !== null) {
      clearTimeout(this.setupScrollTimeoutId);
      this.setupScrollTimeoutId = null;
    }
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private async computeDiffAsync(): Promise<void> {
    const generation = ++this.computeGeneration;
    const isFirstCompute = !this.splitResult;

    this.isComputing = true;

    // Only clear results on first computation — keep showing previous
    // results during re-computation to avoid flicker
    if (isFirstCompute) {
      this.visibleLines = [];
      this.visibleStart = -1;
      this.visibleEnd = -1;
      this.totalHeight = 0;
    }
    this.cdr.markForCheck();

    const result = await this.diffService.computeFullDiffAsync(
      this.oldData,
      this.newData
    );

    // Stale check: another computeDiff was triggered while we were async
    if (generation !== this.computeGeneration) return;

    this.splitResult = result.split;
    this.propResult = result.props;
    this.hasDifferences = result.split.summary.totalChanges > 0;
    this.totalHeight = result.split.lines.length * ROW_HEIGHT;
    this.isComputing = false;

    // Reset virtual scroll range so updateVisibleWindow always recalculates
    this.visibleStart = -1;
    this.visibleEnd = -1;

    this.computeChangeGroups();
    this.updateVisibleWindow(0);
    this.cdr.detectChanges();

    if (this.setupScrollTimeoutId !== null) {
      clearTimeout(this.setupScrollTimeoutId);
    }
    this.setupScrollTimeoutId = setTimeout(() => {
      this.setupScrollTimeoutId = null;
      this.setupSyncScroll();
    }, 0);
  }

  private updateVisibleWindow(scrollTop: number): void {
    const totalLines = this.splitResult?.lines?.length ?? 0;
    if (totalLines === 0) {
      this.visibleLines = [];
      this.topOffset = 0;
      return;
    }

    const panelHeight = this.oldPanelRef?.nativeElement?.clientHeight ?? 400;
    const visibleCount = Math.ceil(panelHeight / ROW_HEIGHT);

    const firstVisible = Math.floor(scrollTop / ROW_HEIGHT);
    const newStart = Math.max(0, firstVisible - BUFFER_ROWS);
    const newEnd = Math.min(
      totalLines,
      firstVisible + visibleCount + BUFFER_ROWS
    );

    if (newStart === this.visibleStart && newEnd === this.visibleEnd) return;

    this.visibleStart = newStart;
    this.visibleEnd = newEnd;
    this.topOffset = newStart * ROW_HEIGHT;

    const lines = this.splitResult!.lines;
    const result: VirtualLine[] = new Array(newEnd - newStart);
    for (let i = newStart; i < newEnd; i++) {
      result[i - newStart] = { line: lines[i], index: i };
    }
    this.visibleLines = result;
  }

  trackByIndex(_: number, item: VirtualLine): number {
    return item.index;
  }

  private computeChangeGroups(): void {
    const groups: ChangeGroup[] = [];
    const lines = this.splitResult?.lines ?? [];
    let groupStart = -1;

    for (let i = 0; i < lines.length; i++) {
      const isChanged = lines[i].type !== 'unchanged';
      if (isChanged && groupStart === -1) {
        groupStart = i;
      } else if (!isChanged && groupStart !== -1) {
        groups.push({ startIndex: groupStart, endIndex: i - 1 });
        groupStart = -1;
      }
    }

    if (groupStart !== -1) {
      groups.push({ startIndex: groupStart, endIndex: lines.length - 1 });
    }

    this.changeGroups = groups;
    this.currentChangeIndex = groups.length > 0 ? 0 : -1;

    if (groups.length > 0) {
      setTimeout(() => this.goToChange(0), 10);
    }
  }

  goToChange(index: number): void {
    if (this.changeGroups.length === 0) return;
    const clamped = Math.max(0, Math.min(index, this.changeGroups.length - 1));
    this.currentChangeIndex = clamped;

    const group = this.changeGroups[clamped];
    if (!this.oldPanelRef?.nativeElement) return;

    const panelEl = this.oldPanelRef.nativeElement;
    const panelHeight = panelEl.clientHeight;
    const targetScrollTop = ROW_HEIGHT * group.startIndex - panelHeight / 2;
    panelEl.scrollTop = Math.max(0, targetScrollTop);
    this.cdr.markForCheck();
  }

  nextChange(): void {
    this.goToChange(this.currentChangeIndex + 1);
  }

  prevChange(): void {
    this.goToChange(this.currentChangeIndex - 1);
  }

  isInActiveGroup(lineIndex: number): boolean {
    if (this.currentChangeIndex < 0 || this.changeGroups.length === 0)
      return false;
    const group = this.changeGroups[this.currentChangeIndex];
    return lineIndex >= group.startIndex && lineIndex <= group.endIndex;
  }

  getRowClass(line: AlignedLine): string {
    switch (line.type) {
      case 'added':
        return 'diff-row-added';
      case 'removed':
        return 'diff-row-removed';
      default:
        return '';
    }
  }

  getOldCellClass(line: AlignedLine): string {
    if (
      line.type === 'removed' &&
      line.oldLine !== null &&
      line.newLine !== null
    ) {
      return 'diff-cell-modified';
    }
    if (line.type === 'removed' && line.oldLine !== null) {
      return 'diff-cell-removed';
    }
    return '';
  }

  getNewCellClass(line: AlignedLine): string {
    if (
      line.type === 'removed' &&
      line.oldLine !== null &&
      line.newLine !== null
    ) {
      return 'diff-cell-modified-new';
    }
    if (line.type === 'added') {
      return 'diff-cell-added';
    }
    return '';
  }

  onAddLineComment(event: MouseEvent, vl: VirtualLine): void {
    event.stopPropagation();
    const selection = window.getSelection();
    const selectedText = selection?.toString().trim();
    if (selectedText && selectedText.length > 0) {
      this.lineComment.emit(selectedText);
      selection?.removeAllRanges();
    } else {
      const content = (vl.line.newLine ?? vl.line.oldLine ?? '').trim();
      this.lineComment.emit(content);
    }
  }

  private onPanelScroll(scrollTop: number): void {
    if (this.rafId !== null) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.updateVisibleWindow(scrollTop);
      this.cdr.detectChanges();
    });
  }

  private setupSyncScroll(): void {
    this.teardownSyncScroll();

    if (!this.oldPanelRef?.nativeElement || !this.newPanelRef?.nativeElement)
      return;

    const oldEl = this.oldPanelRef.nativeElement;
    const newEl = this.newPanelRef.nativeElement;

    this.zone.runOutsideAngular(() => {
      this.oldScrollListener = () => {
        if (this.syncing) return;
        this.syncing = true;
        newEl.scrollTop = oldEl.scrollTop;
        newEl.scrollLeft = oldEl.scrollLeft;
        this.syncing = false;
        this.onPanelScroll(oldEl.scrollTop);
      };

      this.newScrollListener = () => {
        if (this.syncing) return;
        this.syncing = true;
        oldEl.scrollTop = newEl.scrollTop;
        oldEl.scrollLeft = newEl.scrollLeft;
        this.syncing = false;
        this.onPanelScroll(newEl.scrollTop);
      };

      oldEl.addEventListener('scroll', this.oldScrollListener, {
        passive: true,
      });
      newEl.addEventListener('scroll', this.newScrollListener, {
        passive: true,
      });
    });
  }

  private teardownSyncScroll(): void {
    if (this.oldPanelRef?.nativeElement && this.oldScrollListener) {
      this.oldPanelRef.nativeElement.removeEventListener(
        'scroll',
        this.oldScrollListener
      );
    }
    if (this.newPanelRef?.nativeElement && this.newScrollListener) {
      this.newPanelRef.nativeElement.removeEventListener(
        'scroll',
        this.newScrollListener
      );
    }
    this.oldScrollListener = null;
    this.newScrollListener = null;
  }
}
